import httpService from '../httpService';
import { get, post, put } from '../httpMethods';
import { ApiResponse } from '../../types/api';
import {
  DailyStatementEnvelope,
  DailyStatementScheduleInfo,
  DailyStatementSummary,
  ReportDefinition,
  ReportPrintOptions,
  ReportResult,
  RunReportDto,
} from '../../types/reports';

export interface ReportExportResult {
  blob: Blob;
  filename: string;
}

function buildQuery(filters: RunReportDto): string {
  const params = new URLSearchParams();
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.set('dateTo', filters.dateTo);
  if (filters.dateBasis) params.set('dateBasis', filters.dateBasis);
  if (filters.departmentId) params.set('departmentId', filters.departmentId);
  if (filters.doctorId) params.set('doctorId', filters.doctorId);
  if (filters.patientId) params.set('patientId', filters.patientId);
  if (filters.categoryId) params.set('categoryId', filters.categoryId);
  if (filters.serviceType) params.set('serviceType', filters.serviceType);
  if (filters.diagnosticCategory) params.set('diagnosticCategory', filters.diagnosticCategory);
  if (filters.groupBy) params.set('groupBy', filters.groupBy);
  // What the PDF / print copy contains (hidden items + custom labels) is saved
  // server-side per report, so the export request only needs the filters.
  return params.toString();
}

export const reportsService = {
  getReports: (): Promise<ApiResponse<ReportDefinition[]>> => {
    return get<ReportDefinition[]>('/reports');
  },

  runReport: (id: string, dto: RunReportDto): Promise<ApiResponse<ReportResult>> => {
    return post<ReportResult>(`/reports/${id}/run`, dto);
  },

  /** The saved print layout for a report (shared by every user). */
  getPrintOptions: (id: string): Promise<ApiResponse<ReportPrintOptions>> => {
    return get<ReportPrintOptions>(`/reports/${id}/print-options`);
  },

  /** Replace the report's shared print layout. */
  savePrintOptions: (
    id: string,
    options: ReportPrintOptions,
  ): Promise<ApiResponse<ReportPrintOptions>> => {
    return put<ReportPrintOptions>(`/reports/${id}/print-options`, options);
  },

  /** Download a generated report file (pdf | excel | csv) as a Blob. */
  exportReport: async (
    id: string,
    format: string,
    filters: RunReportDto = {},
  ): Promise<ReportExportResult> => {
    const query = buildQuery(filters);
    // No client-side cap: the server renders the whole document (a month of
    // details can be hundreds of pages) and a PDF export legitimately takes
    // longer than the 30 s default, which would abort a request the backend
    // goes on to finish. The server bounds each step itself.
    const response = await httpService.get(`/reports/${id}/export?format=${format}${query ? `&${query}` : ''}`, {
      responseType: 'blob',
      timeout: 0,
    });

    const blob = response.data as Blob;
    const filename = reportsService.filenameFromHeaders(response.headers?.['content-disposition'], id, format);
    return { blob, filename };
  },

  /**
   * The printable A4 document for a report (letterhead plus the saved print
   * layout). Returned as HTML so the caller can open it in a new window; the
   * document triggers the browser print dialog itself.
   */
  printReport: async (id: string, filters: RunReportDto = {}): Promise<string> => {
    const query = buildQuery(filters);
    const response = await httpService.get(`/reports/${id}/export?format=print${query ? `&${query}` : ''}`, {
      responseType: 'text',
      headers: { Accept: 'text/html' },
      // Same as the PDF path: building the document includes the report query.
      timeout: 0,
    });
    return typeof response.data === 'string' ? response.data : String(response.data ?? '');
  },

  /** Freeze (or regenerate) the Daily Statement for a single day. */
  generateDailyStatement: (
    date?: string,
    regenerate = true,
  ): Promise<ApiResponse<DailyStatementEnvelope>> => {
    return post<DailyStatementEnvelope>('/daily-statements/generate', { date, regenerate });
  },

  getDailyStatementSchedule: (): Promise<ApiResponse<DailyStatementScheduleInfo>> => {
    return get<DailyStatementScheduleInfo>('/daily-statements/schedule');
  },

  /** Archived Daily Statement snapshots, newest first. */
  getDailyStatements: (
    dateFrom?: string,
    dateTo?: string,
  ): Promise<ApiResponse<DailyStatementSummary[]>> => {
    const params = new URLSearchParams();
    if (dateFrom) params.set('dateFrom', dateFrom);
    if (dateTo) params.set('dateTo', dateTo);
    const query = params.toString();
    return get<DailyStatementSummary[]>(`/daily-statements${query ? `?${query}` : ''}`);
  },

  filenameFromHeaders(contentDisposition: string | undefined, id: string, format: string): string {
    const extension = format === 'excel' ? 'xls' : format;
    const fallback = `${id}-${new Date().toISOString().slice(0, 10)}.${extension}`;
    if (!contentDisposition) return fallback;
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(contentDisposition);
    return match?.[1] ? decodeURIComponent(match[1]) : fallback;
  },
};
